import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {restRequest} from '../../../api-utils/apiQueries'
import {
  audToCents,
  centsToAud,
  formatPricingLabel,
  PlatformPriceEntry,
} from './platformPricingHelpers'

const UpdatedDriverPrice = ({
  showUpdatedModal,
  toggleUpdatedModal,
  driverPrice,
  onSaved,
}: {
  showUpdatedModal: boolean
  toggleUpdatedModal: () => void
  driverPrice: PlatformPriceEntry[]
  onSaved: () => void
}) => {
  const [priceValues, setPriceValues] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!showUpdatedModal) return
    const initial = driverPrice.reduce<Record<string, string>>((acc, item) => {
      acc[item.key] = centsToAud(item.value).toFixed(2)
      return acc
    }, {})
    setPriceValues(initial)
    setError('')
    setSaving(false)
  }, [driverPrice, showUpdatedModal])

  const handlePriceChange = (key: string, value: string) => {
    setPriceValues((current) => ({...current, [key]: value}))
    setError('')
  }

  const validatePrices = () => {
    for (const item of driverPrice) {
      const rawValue = priceValues[item.key]
      const value = Number(rawValue)
      if (rawValue === '' || !Number.isFinite(value) || value < 0) {
        return `${formatPricingLabel(item.key)} must be a valid price.`
      }
    }
    return ''
  }

  const handleUpdatedValue = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validatePrices()
    if (validationError) {
      setError(validationError)
      return
    }

    const data = driverPrice.reduce<Record<string, number>>((acc, item) => {
      acc[item.key] = audToCents(priceValues[item.key])
      return acc
    }, {})

    setSaving(true)
    setError('')
    try {
      const response: any = await restRequest.updatedPriceValue(data)
      if (response.data) {
        onSaved()
        toggleUpdatedModal()
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to update pricing.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={showUpdatedModal} className='modal-md' onHide={toggleUpdatedModal}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Update platform pricing</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleUpdatedValue}>
          <div className='text-muted fs-7 mb-5'>
            Enter AUD values per seat. The system stores them internally as cents.
          </div>

          <div className='d-flex flex-column gap-4'>
            {driverPrice.map((item) => (
              <div key={item.key}>
                <label className='form-label fw-semibold'>{formatPricingLabel(item.key)}</label>
                <div className='input-group'>
                  <span className='input-group-text'>$</span>
                  <input
                    type='number'
                    min='0'
                    step='0.01'
                    inputMode='decimal'
                    value={priceValues[item.key] ?? ''}
                    onChange={(e) => handlePriceChange(item.key, e.target.value)}
                    className='form-control'
                    placeholder='0.00'
                  />
                </div>
              </div>
            ))}
          </div>

          {error ? <div className='alert alert-danger py-3 mt-5 mb-0'>{error}</div> : null}

          <Modal.Footer className='px-0 pb-0 mt-5'>
            <Button
              variant='secondary'
              size='sm'
              onClick={toggleUpdatedModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type='submit' variant='primary' size='sm' disabled={saving}>
              {saving ? (
                <span className='spinner-border spinner-border-sm align-middle me-2' role='status' />
              ) : null}
              {saving ? 'Saving...' : 'Save pricing'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default UpdatedDriverPrice
